// The campaign / audience / creative build-and-publish reads.
//
// Two of these are the reason the `private` namespace exists. `pixels.samples`
// and `creatives.reviewPackages` both carry PII — the second holds
// `approvals[].email`, `.ip_address` and `.user_agent` — and both were written
// as bare literal keys matching no namespace at all. `private` now states their
// short-memory and logout behavior, and `pii: true` keeps the registry audit
// explicit. No server response is persisted by any namespace.

import { api } from '@/api/client';
import { CADENCE, FRESHNESS, defineQuery } from '@/api/resource';
import { campaignLaterLaneInFlight, countInFlightOperations } from '@/lib/campaign-sync-outcome';
import type { CampaignFlightExtensionsResponse, CreativeRevisionsResponse } from '@/api/types';
import type { CreativeReadiness, CreativeSpecs } from '@/api/types.gen';
import { extensionIsMoving, extensionIsOpen } from '@/lib/flight-extension';
import type {
  AudienceEstimateSize,
  AudiencePlannerBenchmarks,
  AudiencePlannerPreviewInput,
  AudiencePreview,
  AudiencePreviewInput,
  AudienceSyncStatus,
  CampaignForecast,
  CampaignForecastInput,
  CampaignPlanResponse,
  CampaignPreflight,
  CampaignSyncStatus,
  CreativePreviewStatus,
  ManageAdSet,
} from '@/api/manage-types';
import type {
  CampaignCreativeAddition,
  CreativeReviewPackageSummary,
  MetaPreviewBundle,
  PixelSamplesResponse,
} from '@/api/types.gen';

const encoded = encodeURIComponent;

/**
 * Revision-controlled, and the one domain-backed read that also polls: the
 * plan embeds two queue-consumer journals (slot changes, roll-outs), and a
 * change the user just confirmed otherwise sat on "queued" until the 60 s
 * revision manifest noticed (2026-09-18). The interval is a payload function
 * that returns `false` when nothing is in flight; `refetchInterval` fires
 * independent of `staleTime`, so the poll works on a slot born fresh forever.
 * `resources.taxonomy.test.ts` names this exception.
 */
export const campaignPlan = defineQuery({
  namespace: 'authoritative',
  name: 'campaigns.plan',
  domains: ['campaigns', 'audiences', 'creatives'],
  keyOf: (id: string) => [id],
  cadence: (raw: CampaignPlanResponse | undefined) =>
    raw && countInFlightOperations(raw.plan) > 0 ? CADENCE.urgent : false,
  request: (id: string, signal) =>
    api.get<CampaignPlanResponse>(`/campaigns/${encoded(id)}/plan`, { signal }),
  unwrap: (r) => r.plan,
});

/**
 * Every D1 dependency that can change launch readiness is revision-controlled.
 * `catalog` because `build_preflight` checks the campaign's production still
 * exists; `pixels` because the audience scope gate in front of it refuses a
 * rule tree naming a pixel that no longer does.
 */
export const campaignPreflight = defineQuery({
  namespace: 'derived',
  name: 'campaigns.preflight',
  domains: ['catalog', 'campaigns', 'audiences', 'creatives', 'pixels', 'configuration'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<CampaignPreflight>(`/campaigns/${encoded(id)}/preflight`, { signal }),
});

export const campaignAdSets = defineQuery({
  namespace: 'authoritative',
  name: 'campaigns.adSets',
  domains: ['campaigns'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<{ ad_sets: ManageAdSet[] }>(`/campaigns/${encoded(id)}/ad-sets`, { signal }),
  unwrap: (r) => r.ad_sets,
});

/** User-driven recompute; a minute is long enough to survive a re-render storm. */
export const campaignForecast = defineQuery({
  namespace: 'derived',
  name: 'campaigns.forecast',
  // Historical/provider inputs are operational, not revisioned D1 rows.
  volatile: true,
  freshness: FRESHNESS.minute,
  request: (request: CampaignForecastInput | null, signal) =>
    api.post<CampaignForecast>('/campaigns/forecast', { body: request, signal }),
});

/**
 * Publish progress. The cadence comes from the CALL SITE because whether a
 * status is terminal depends on which provider the caller is watching — a
 * campaign can be synced to Meta while TTD is still in flight.
 */
export const campaignSyncStatus = defineQuery({
  namespace: 'realtime',
  name: 'campaigns.syncStatus',
  volatile: true,
  keyOf: (id: string) => [id],
  // Urgent while a slot change or roll-out is writing its per-audience
  // journal rows, so the card's steps move as they land; the call site's
  // settled tiers take over once nothing is running.
  cadence: (status: CampaignSyncStatus | undefined) =>
    status && campaignLaterLaneInFlight(status) ? CADENCE.urgent : CADENCE.fast,
  request: (id: string, signal) =>
    api.get<CampaignSyncStatus>(`/campaigns/${encoded(id)}/sync-status`, { signal }),
});

export const audiencePreview = defineQuery({
  namespace: 'derived',
  name: 'audiences.preview',
  // Backed by live audience populations in the analytics stores.
  volatile: true,
  freshness: FRESHNESS.minute,
  request: (request: AudiencePreviewInput | null, signal) =>
    api.post<AudiencePreview>('/audiences/preview', { body: request, signal }),
});

/**
 * The live Planner analysis POST.
 *
 * `retry: false`, AGAINST the namespace default of 1 — and this line is a
 * restored incident fix, not a preference. The failure mode here is the 25s
 * server-side budget expiring. The server now cancels its query at that
 * budget, but a retry of the same body meets the same deadline: it cannot
 * succeed where the first attempt timed out, and it doubles the load on a
 * shared query cluster that has no resource groups to shield anyone else —
 * the load that was the reason for the timeout. (When this fix was first made,
 * the abandoned work was not even cancelled, and one click became ~75s of
 * overlapping work.) The user can press Analyze again if they want one.
 *
 * The original hook carried this option and this reasoning. Moving the read
 * into the registry dropped both, silently — the sweep saw an options key it
 * had a namespace default for and did not ask why the default had been
 * overridden. `resources.taxonomy.test.ts` now asserts it, so the next sweep
 * fails instead of reverting an incident fix a second time.
 */
export const audiencePlannerPreview = defineQuery({
  namespace: 'derived',
  name: 'audiences.plannerPreview',
  volatile: true,
  freshness: FRESHNESS.minute,
  retry: false,
  request: (request: AudiencePlannerPreviewInput | null, signal) =>
    api.post<AudiencePreview>('/audiences/planner-preview', { body: request, signal }),
});

/**
 * The comparator profile behind the planner's benchmarks.
 *
 * `keepPreviousData` holds the all-buyers profile on screen while a newly
 * selected production loads. The screen hides the old production's profile
 * while the placeholder is in use, so it can never be mislabelled as the new
 * comparator — which is the reason this is the only place in the app that keeps
 * previous data at all.
 */
export const audiencePlannerBenchmarks = defineQuery({
  namespace: 'derived',
  name: 'audiences.plannerBenchmarks',
  volatile: true,
  keyOf: (productionId: string | null) => [productionId],
  freshness: FRESHNESS.fiveMinutes,
  keepPreviousData: true,
  request: (productionId: string | null, signal) =>
    api.get<AudiencePlannerBenchmarks>(
      `/audiences/planner-benchmarks${productionId ? `?production_id=${encoded(productionId)}` : ''}`,
      { signal },
    ),
});

export const audienceEstimateSize = defineQuery({
  namespace: 'derived',
  name: 'audiences.estimateSize',
  volatile: true,
  freshness: FRESHNESS.minute,
  request: (request: AudiencePreviewInput | null, signal) =>
    api.post<AudienceEstimateSize>('/audiences/estimate-size', { body: request, signal }),
});

export const audienceSyncStatus = defineQuery({
  namespace: 'realtime',
  name: 'audiences.syncStatus',
  volatile: true,
  keyOf: (id: string) => [id],
  cadence: CADENCE.fast,
  request: (id: string, signal) =>
    api.get<AudienceSyncStatus>(`/audiences/${encoded(id)}/sync-status`, { signal }),
});

/** Campaign launch gate read pinned to the audience revision in the plan. */
export const audienceRevisionSyncStatus = defineQuery({
  namespace: 'realtime',
  name: 'audiences.revisionStatus',
  volatile: true,
  keyOf: (input: { id: string; revision: number }) => [input.id, input.revision],
  cadence: CADENCE.fast,
  request: (input: { id: string; revision: number }, signal) =>
    api.get<AudienceSyncStatus>(
      `/audiences/${encoded(input.id)}/sync-status?revision=${input.revision}`,
      { signal },
    ),
});

/**
 * Provider-rendered previews. Focus refetch is on against the namespace
 * default: a preview that expired while the tab was in the background should
 * re-render on return rather than showing a dead image.
 *
 * This was once persisted while polling against `refresh_after`, which is a
 * Meta-signed URL with an expiry. It is now explicitly volatile so an expired
 * provider URL cannot survive a reload.
 */
export const metaCreativePreviews = defineQuery({
  namespace: 'realtime',
  name: 'creatives.metaPreviews',
  volatile: true,
  keyOf: (id: string) => [id],
  freshness: FRESHNESS.minute,
  refetchOnWindowFocus: true,
  cadence: (bundle: MetaPreviewBundle | undefined) => {
    if (!bundle || !bundle.fresh) return CADENCE.fast;
    const refreshAt = bundle.variants
      .map((variant) => variant.refresh_after)
      .filter((value): value is string => !!value)
      .map((value) => Date.parse(value))
      .sort((left, right) => left - right)[0];
    // Floored at the base cadence so a provider timestamp already in the past
    // cannot turn this into a tight loop, and capped at an hour so a client clock
    // running behind the server cannot push the refresh past the URLs' expiry.
    if (refreshAt) return Math.min(FRESHNESS.hour, Math.max(CADENCE.fast, refreshAt - Date.now()));
    return false;
  },
  request: (id: string, signal) =>
    api.get<MetaPreviewBundle>(`/creatives/${encoded(id)}/meta-previews`, { signal }),
});

/**
 * Every revision of a creative — the archive. `authoritative` and domain-backed:
 * a revision's status, the campaigns that pin it and its provider ids all move
 * through `creatives` and `campaigns` writes.
 */
export const creativeRevisions = defineQuery({
  namespace: 'authoritative',
  name: 'creatives.revisions',
  domains: ['creatives', 'campaigns'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<CreativeRevisionsResponse>(`/creatives/${encoded(id)}/revisions`, { signal }),
  unwrap: (r) => r.revisions,
});

/**
 * What each channel's creative files and copy must be (`GET /creative-specs`): the shared
 * catalogs the server enforces. They change only with an API release, which the Pages release
 * checker cannot see, so they are re-read when older than an hour rather than kept forever.
 */
export const creativeSpecs = defineQuery({
  namespace: 'authoritative',
  name: 'creatives.specs',
  volatile: true,
  freshness: FRESHNESS.hour,
  keyOf: () => [],
  request: (_: void, signal) => api.get<CreativeSpecs>('/creative-specs', { signal }),
});

/** Whether a creative could be submitted for review now, and the problems if not. */
export const creativeReadiness = defineQuery({
  namespace: 'authoritative',
  name: 'creatives.readiness',
  domains: ['creatives'],
  keyOf: (id: string) => [id],
  request: (id: string, signal) =>
    api.get<CreativeReadiness>(`/creatives/${encoded(id)}/readiness`, { signal }),
});

export const creativePreviewStatus = defineQuery({
  namespace: 'realtime',
  name: 'creatives.previewStatus',
  volatile: true,
  keyOf: (id: string) => [id],
  cadence: CADENCE.fast,
  request: (id: string, signal) =>
    api.get<CreativePreviewStatus>(`/creatives/${encoded(id)}/preview-status`, { signal }),
});

/**
 * Review links and the decisions recorded against them.
 *
 * `private`: `approvals[]` carries `email`, `ip_address` and `user_agent`,
 * `reviewers[]` the addresses that confirmed, and a live link's `share_url` is
 * a bearer credential. No query response is persisted, and this payload also
 * leaves memory quickly. Expired packages are included — the approval record
 * attached to a dead link is exactly the thing worth keeping.
 */
export const creativeReviewPackages = defineQuery({
  namespace: 'private',
  name: 'creatives.reviewPackages',
  domains: ['creatives'],
  pii: true,
  keyOf: (creativeId: string) => [creativeId],
  request: (creativeId: string, signal) =>
    api.get<CreativeReviewPackageSummary[]>(
      `/creatives/${encoded(creativeId)}/review-packages`,
      { signal },
    ),
});

/**
 * Recent raw events for one pixel.
 *
 * `private` for the same reason: the screen's own comment says these are
 * "fetched live (never KV-cached: may hold PII)". That intent is now the
 * namespace rather than a comment beside a bare literal key.
 */
export const pixelSamples = defineQuery({
  namespace: 'private',
  name: 'pixels.samples',
  volatile: true,
  pii: true,
  keyOf: (id: string) => [id],
  freshness: FRESHNESS.minute,
  cadence: CADENCE.lazy,
  request: (id: string, signal) =>
    api.get<PixelSamplesResponse>(`/pixels/${encoded(id)}/samples`, { signal }),
});

/**
 * The durable journal of creatives being added to one published campaign.
 *
 * `volatile`, and the poll is declared HERE rather than at the call site: the
 * rows move because a queue consumer writes them, so how closely to watch them
 * is a fact about this read, not about who happens to be looking. The cadence
 * is a function of the payload, the same shape `flow.lineage` uses — watch an
 * accepted addition until the provider work lands, then stop asking. A new
 * request re-opens the poll, because the mutation invalidates this key.
 */
export const campaignCreativeAdditions = defineQuery({
  namespace: 'realtime',
  name: 'campaigns.creativeAdditions',
  volatile: true,
  keyOf: (id: string) => [id],
  cadence: (rows: CampaignCreativeAddition[] | undefined) =>
    rows?.some((row) => row.status === 'queued' || row.status === 'running')
      ? CADENCE.urgent
      : false,
  request: (id: string, signal) =>
    api.get<CampaignCreativeAddition[]>(`/campaigns/${encoded(id)}/creative-additions`, { signal }),
});

/**
 * A campaign's Extend-flight journal (0030), newest first, and whether Extend is on for its
 * provider (2026-10-21).
 *
 * `realtime`, and the poll is the payload's like the additions journal above: watched
 * closely while SAM works on a row (queued, running, or committed until its follow-up is
 * verified), slowly while a row waits on a person or on discovery (failed and still holding
 * the campaign, or a create SAM cannot confirm, which can last days), not at all otherwise.
 * The slow poll is what turns "Extending…" into "Extension needs attention" after an hour.
 */
export const campaignFlightExtensions = defineQuery({
  namespace: 'realtime',
  name: 'campaigns.flightExtensions',
  volatile: true,
  keyOf: (id: string) => [id],
  cadence: (response: CampaignFlightExtensionsResponse | undefined) => {
    const rows = response?.extensions ?? [];
    if (rows.some(extensionIsMoving)) return CADENCE.fast;
    return rows.some(extensionIsOpen) ? CADENCE.ambient : false;
  },
  request: (id: string, signal) =>
    api.get<CampaignFlightExtensionsResponse>(`/campaigns/${encoded(id)}/flight-extensions`, { signal }),
});
